import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth, storage } from '../../firebase/firebase-config';
import { Button, Spinner } from 'react-bootstrap';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import User from '../../models/User';
import ModulePageHeader from '../common/ModulePageHeader';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { fetchUnitsForTenant } from '../../services/unitsService';
import { isTenantAdminForTenant } from '../../platform/membershipPermissions';
import ProfileHub from './ProfileHub';
import ProfileEdit from './ProfileEdit';
import ProfileSecurity from './ProfileSecurity';
import AccountSettings from './AccountSettings';
import { isProfileComplete, profileCompletionErrorMessage } from '../../utils/profileUtils';
import {
  EMAIL_VERIFICATION_SENT_MESSAGE,
  isValidProfileEmail,
  normalizeProfileEmail,
  requestProfileEmailUpdate,
} from '../../services/profileEmailService';
import useBrowserBackStack from '../../hooks/useBrowserBackStack';
import './ProfileHub.css';

const VIEW_TITLES = {
  hub: 'Profil',
  edit: 'Osebni podatki',
  security: 'Varnost',
  account: 'Račun',
};

function Profile({ onProfileComplete }) {
  const { setUser, setProfileComplete: setContextProfileComplete } = useUser();
  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();

  const [view, setView] = useState('hub');
  const [profile, setProfile] = useState(new User({}));
  const [savedProfile, setSavedProfile] = useState(new User({}));
  const [isAdmin, setIsAdmin] = useState(false);
  const [units, setUnits] = useState([]);
  const [profileComplete, setProfileComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const wasIncompleteRef = useRef(false);
  const [showActivationMessage, setShowActivationMessage] = useState(false);

  const resetToHub = useCallback(() => setView('hub'), []);
  const closeSubView = useBrowserBackStack(profileComplete && view !== 'hub', resetToHub);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!auth.currentUser) return;

      try {
        const docRef = doc(db, 'users', auth.currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userProfile = User.fromFirestore(docSnap);
          if (!userProfile.email?.trim() && auth.currentUser?.email) {
            userProfile.email = auth.currentUser.email;
          }
          setProfile(userProfile);
          setSavedProfile(userProfile);
          setIsAdmin(isTenantAdminForTenant(memberships, tenantId, userProfile));

          const complete = isProfileComplete(userProfile);
          setProfileComplete(complete);
          wasIncompleteRef.current = !complete;
          if (!complete) {
            setView('edit');
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
      setLoading(false);
    };

    const fetchUnits = async () => {
      if (!auth.currentUser || !tenantId) return;

      try {
        const docRef = doc(db, 'users', auth.currentUser.uid);
        const docSnap = await getDoc(docRef);
        const currentUser = docSnap.exists() ? User.fromFirestore(docSnap) : null;
        const unitsList = await fetchUnitsForTenant(tenantId, {
          user: currentUser,
          memberships,
        });
        setUnits(unitsList);
      } catch (error) {
        console.error('Error fetching units:', error);
      }
    };

    fetchProfile();
    fetchUnits();
  }, [tenantId, memberships]);

  useEffect(() => () => {
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
  }, [photoPreviewUrl]);

  const clearPendingPhoto = () => {
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPendingPhotoFile(null);
    setPhotoPreviewUrl(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProfile((prev) => {
      const updatedProfile = { ...prev, [name]: value };
      if (name === 'firstName' || name === 'lastName') {
        updatedProfile.displayName = `${updatedProfile.firstName} ${updatedProfile.lastName}`.trim();
      }
      return new User(updatedProfile);
    });
  };

  const isProfileDirty = () => {
    if (pendingPhotoFile) return true;
    const fields = ['firstName', 'lastName', 'email', 'phone', 'jobPosition', 'workArea', 'unit', 'role', 'displayName', 'chatColor'];
    return fields.some((field) => {
      if (field === 'email') {
        return normalizeProfileEmail(profile.email) !== normalizeProfileEmail(savedProfile.email);
      }
      return (profile[field] || '') !== (savedProfile[field] || '');
    });
  };

  const handleCancel = () => {
    clearPendingPhoto();
    setProfile(new User(savedProfile));
    setSaveError('');
    setSaveSuccess('');
  };

  const uploadPendingPhoto = async () => {
    const storageRefInstance = ref(storage, `profileImages/${auth.currentUser.uid}`);
    const snapshot = await uploadBytes(storageRefInstance, pendingPhotoFile);
    return getDownloadURL(snapshot.ref);
  };

  const handleSave = async () => {
    if (!auth.currentUser || saving) return;

    if (!isProfileComplete(profile)) {
      setSaveError(profileCompletionErrorMessage(profile));
      return;
    }

    const normalizedEmail = normalizeProfileEmail(profile.email);
    if (!normalizedEmail || !isValidProfileEmail(normalizedEmail)) {
      setSaveError('Prosimo, vnesite veljaven elektronski naslov.');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      let profileToSave = new User({
        ...profile.toFirestore(),
        uid: auth.currentUser.uid,
        email: normalizedEmail,
      });
      let photoUploadFailed = false;
      let emailVerificationSent = false;

      const emailChanged = normalizedEmail !== normalizeProfileEmail(savedProfile.email);
      if (emailChanged) {
        emailVerificationSent = await requestProfileEmailUpdate(normalizedEmail);
      }

      if (pendingPhotoFile) {
        try {
          const photoURL = await uploadPendingPhoto();
          profileToSave = new User({
            ...profileToSave.toFirestore(),
            uid: auth.currentUser.uid,
            photoURL,
          });
        } catch (uploadError) {
          console.error('Failed to upload image:', uploadError);
          photoUploadFailed = true;
        }
      }

      const docRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(docRef, profileToSave.toFirestore(), { merge: true });

      if (!photoUploadFailed) {
        clearPendingPhoto();
      }

      setProfile(profileToSave);
      setUser(profileToSave);
      const nowComplete = isProfileComplete(profileToSave);
      setProfileComplete(nowComplete);
      setContextProfileComplete(nowComplete);
      setSavedProfile(new User(profileToSave));

      if (wasIncompleteRef.current && nowComplete && !isAdmin) {
        setShowActivationMessage(true);
      }

      wasIncompleteRef.current = !nowComplete;

      if (nowComplete && typeof onProfileComplete === 'function') {
        onProfileComplete(profileToSave);
      }

      if (photoUploadFailed) {
        setSaveError(
          'Podatki profila so shranjeni, vendar slike ni bilo mogoče naložiti. Poskusite znova ali shranite brez slike.',
        );
      } else if (emailVerificationSent) {
        setSaveSuccess(`Profil je bil uspešno shranjen. ${EMAIL_VERIFICATION_SENT_MESSAGE}`);
      } else {
        setSaveSuccess('Profil je bil uspešno shranjen.');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError(error.message || 'Shranjevanje profila ni uspelo. Poskusite znova.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
    }

    setPendingPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  const onHub = view === 'hub' && profileComplete;
  const headerTitle = VIEW_TITLES[view] || VIEW_TITLES.hub;

  const renderContent = () => {
    if (view === 'edit') {
      return (
        <ProfileEdit
          profile={profile}
          savedProfile={savedProfile}
          isAdmin={isAdmin}
          units={units}
          profileComplete={profileComplete}
          pendingPhotoFile={pendingPhotoFile}
          photoPreviewUrl={photoPreviewUrl}
          saving={saving}
          saveError={saveError}
          saveSuccess={saveSuccess}
          showActivationMessage={showActivationMessage}
          onChange={handleChange}
          onImageSelect={handleImageSelect}
          onSave={handleSave}
          onCancel={handleCancel}
          isProfileDirty={isProfileDirty}
        />
      );
    }

    if (view === 'security') {
      return <ProfileSecurity />;
    }

    if (view === 'account') {
      return (
        <div className="profile-subpage">
          <AccountSettings showHeading={false} />
        </div>
      );
    }

    return (
      <ProfileHub
        profile={profile}
        photoPreviewUrl={photoPreviewUrl}
        onSelectView={setView}
        tenantDisplayName={config?.displayName}
      />
    );
  };

  return (
    <div className="platform-module-container profile-container">
        <ModulePageHeader
          eyebrow={!onHub ? 'Profil' : undefined}
          title={headerTitle}
          subtitle={onHub ? 'Vaš osebni kotiček v sindikalnem portalu.' : undefined}
          actions={
            profileComplete && view !== 'hub' ? (
              <Button variant="primary" onClick={closeSubView}>
                Nazaj
              </Button>
            ) : null
          }
        />
        {renderContent()}
    </div>
  );
}

Profile.defaultProps = {
  onProfileComplete: () => {},
};

export default Profile;
